import type { DocumentType, ProjectCheckFinding } from '../types';
import type { ValidationMessage } from './invoiceLogic';
import { istTorGesperrt } from './allocationGate';
import { TorMeldung, kappeStack, meldeUeberHintergrundkanal, sicherMelden } from './allocationGateShadow';
import {
    ENTWURF_NICHT_LESBAR,
    EntwurfJournalNichtLesbarError,
    EntwurfNachlaufError,
    JOURNAL_NACHLAUF,
    meldeKontingent,
} from './entwurfAusJournal';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SPERREN BEIM AUSSTELLEN ZAEHLEN (nur melden, keine Wirkung)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Beim Ausstellen scheitern Belege an mehreren Sperren: Pauschalriegel,
 * Fehlstand, Paketposition (planProjectIssue), Projektcheck, fremde
 * Projektsperre (Phase A), Pflichtangaben (validateInvoice) und an allem, was
 * sonst wirft. Der Nutzer sieht eine Meldung, gezaehlt wurde bisher nur die
 * Sperre am Tor (S5a, allocationGate.ts). Ohne Zahlen laesst sich weder sagen,
 * woran Kollegen haengen bleiben, noch vor Stufe 3d vergleichen, ob das Tor
 * mehr sperrt als die alten Riegel.
 *
 * Deshalb meldet `issueInvoice` (invoiceService) JEDEN gescheiterten
 * Ausstellversuch ueber den Hintergrundkanal nach `bug_reports`; die
 * Ueberwachungsabfrage zaehlt daraus Sperren je Art. Die Ausstellung selbst
 * aendert sich nicht: dieselben Fehler, dieselben Texte, dieselbe Reihenfolge.
 * Die Meldung wird erst NACH dem Scheitern gebaut, wirft nie und laesst den
 * Originalfehler unberuehrt.
 *
 * Nicht gemeldet wird, was sich schon selbst meldet: die Sperre am Tor
 * (`TorGesperrtError`) und die beiden Fehler des Entwurfs aus dem Journal
 * (`EntwurfJournalNichtLesbarError`, `EntwurfNachlaufError`). Doppelt
 * gezaehlt stuende eine Tor-Sperre in zwei Zeilen der Ueberwachung, und der
 * Vergleich vor Stufe 3d stimmte nicht mehr.
 *
 * Grenze: Was der Editor schon VOR dem Klick anzeigt, sperrt dort den Knopf
 * und erreicht `issueInvoice` nie. Gezaehlt wird, was am Ausstellpfad
 * scheitert, nicht jeder Befund, den die Maske je gezeigt hat.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Die Arten
// ═══════════════════════════════════════════════════════════════════════════

export type AusstellSperrArt =
    | 'pauschalriegel'
    | 'fehlstand'
    | 'paketposition'
    | 'pflichtangaben'
    | 'projektcheck_rot'
    | 'projektcheck_kenntnisnahme'
    | 'projekt_in_bearbeitung'
    | 'andere';

/** Was eine Sperre fuer die Meldung ausmacht. */
export interface AusstellSperre {
    art: AusstellSperrArt;
    /** Die Codes des Fehlers, in der Reihenfolge ihres Auftretens, ohne Doppel. */
    codes: string[];
    /** Ein Satz fuer die Meldung — gekappt, ohne Zeilenumbruch. */
    text: string;
}

/**
 * Welcher Code welcher Riegel ist. Die Codes stehen in invoiceLogic
 * (flatRateIssues, missingUsedIssues, componentLineIssues) als feste Texte,
 * keiner wird dort zusammengesetzt. Alles uebrige aus validateInvoice sind
 * Pflichtangaben.
 */
export const PAUSCHALRIEGEL_CODES: ReadonlySet<string> = new Set([
    'flat_covered_line_billed',
    'flat_month_duplicate',
    'flat_month_unbilled',
    'flat_month_unbooked',
]);
export const FEHLSTAND_CODES: ReadonlySet<string> = new Set(['logged_exceeds_used']);
export const PAKETPOSITION_CODES: ReadonlySet<string> = new Set(['component_line_incomplete']);

/**
 * Warum der Projektcheck sperrt — die beiden Wuerfe in issueInvoice: rote
 * Ampel, oder gelbe Ampel ohne Kenntnisnahme. Ein Feld am Fehler statt einer
 * Erkennung am Text: der Text darf sich aendern, die Zaehlung nicht.
 */
export type ProjektcheckSperrGrund = 'rot' | 'kenntnisnahme';

/**
 * Ein anderer hat das Projekt offen (fremde Projektsperre, Phase A von
 * issueInvoice). Bis hierher warf die Stelle ein schlichtes `Error` mit
 * derselben Nachricht; die eigene Klasse macht die Sperre zaehlbar, ohne den
 * Text zu aendern — die Maske zeigt weiter `message`.
 */
export class ProjektInBearbeitungError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'ProjektInBearbeitungError';
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Einordnung — rein
// ═══════════════════════════════════════════════════════════════════════════

/** Hoechstens so viele Zeichen Text in der Meldung. */
const TEXT_GRENZE = 300;

/**
 * Ein Fehler im Sinne der Zaehlung: ein `Error` oder ein Objekt mit Text in
 * `message`. Supabase liefert seine Fehler als schlichte Objekte
 * (`{ code, message, … }`), und issueInvoice wirft sie unveraendert weiter
 * (Statusanspruch, Snapshot, finales Update) — auch das ist ein gescheiterter
 * Ausstellversuch.
 */
/** Was die Einordnung an einem Fehler liest — alles ausser `message` kann fehlen. */
interface FehlerArtig {
    message: string;
    name?: unknown;
    code?: unknown;
    /** InvoiceValidationError */
    issues?: unknown;
    /** ProjectCheckBlockedError */
    grund?: unknown;
    result?: { canIssue?: unknown; findings?: unknown } | null;
}

const istFehler = (e: unknown): e is FehlerArtig =>
    e instanceof Error ||
    (typeof e === 'object' && e !== null && typeof (e as { message?: unknown }).message === 'string');

/** Melden sich selbst — Tor und Entwurf aus dem Journal, am Code auch ueber Modulgrenzen erkennbar. */
const meldetSichSelbst = (e: FehlerArtig): boolean => {
    if (istTorGesperrt(e)) return true;
    if (e instanceof EntwurfJournalNichtLesbarError || e instanceof EntwurfNachlaufError) return true;
    return e.code === ENTWURF_NICHT_LESBAR || e.code === JOURNAL_NACHLAUF;
};

/*
 * InvoiceValidationError und ProjectCheckBlockedError liegen in
 * invoiceService, das dieses Modul importiert — ein Import zurueck waere ein
 * Kreis. Erkannt werden sie deshalb an ihrem Namen und ihrer Form, wie die
 * Tests sie ohnehin pruefen (`thrown.name === 'InvoiceValidationError'`).
 */
const istValidierung = (e: FehlerArtig): boolean =>
    e.name === 'InvoiceValidationError' && Array.isArray(e.issues);

const istProjektcheck = (e: FehlerArtig): boolean => e.name === 'ProjectCheckBlockedError';

const istProjektInBearbeitung = (e: FehlerArtig): boolean =>
    e instanceof ProjektInBearbeitungError || e.name === 'ProjektInBearbeitungError';

/** Ohne Doppel, in der Reihenfolge des ersten Auftretens. */
const einmal = (werte: string[]): string[] => [...new Set(werte.filter((w) => w !== ''))];

/** Erste Zeile, ohne Zeilenumbruch, hoechstens TEXT_GRENZE Zeichen. */
const ersteZeile = (s: string): string => kappeText(String(s ?? '').split(/\r?\n/, 1)[0].trim());

const kappeText = (s: string): string => {
    const flach = s.replace(/\s*[\r\n]+\s*/g, ' ').trim();
    return flach.length > TEXT_GRENZE ? `${flach.slice(0, TEXT_GRENZE - 1)}…` : flach;
};

const validierungsSperre = (issues: ValidationMessage[]): AusstellSperre => {
    const fehler = issues.filter((m) => m?.level === 'error');
    const codes = einmal(fehler.map((m) => String(m.code ?? '')));
    // Rangfolge bei gemischten Codes: das, was am Projekt haengt, vor den
    // Pflichtangaben — es ist der seltenere und teurere Befund.
    const art: AusstellSperrArt = codes.some((c) => PAUSCHALRIEGEL_CODES.has(c))
        ? 'pauschalriegel'
        : codes.some((c) => FEHLSTAND_CODES.has(c))
          ? 'fehlstand'
          : codes.some((c) => PAKETPOSITION_CODES.has(c))
            ? 'paketposition'
            : 'pflichtangaben';
    // Nur Codes und Anzahl, kein Meldungstext: die Texte nennen Positionen,
    // Kunden und Anschriften, und in bug_reports gehoert davon nichts
    // (Codex-Review, P2). Fuer die Zaehlung reicht der Code.
    return { art, codes, text: `${fehler.length} Befund(e)` };
};

const projektcheckSperre = (e: FehlerArtig): AusstellSperre => {
    // Ohne Grund (ein Aufrufer, der ihn nicht setzt): die Ampel selbst
    // entscheidet — rot laesst sich nicht ausstellen, gelb nur mit Notiz.
    const grund: ProjektcheckSperrGrund =
        e.grund === 'rot' || e.grund === 'kenntnisnahme'
            ? e.grund
            : e.result?.canIssue === false
              ? 'rot'
              : 'kenntnisnahme';
    const ampel = grund === 'rot' ? 'red' : 'yellow';
    const roh = e.result?.findings;
    const befunde: Partial<ProjectCheckFinding>[] = Array.isArray(roh) ? roh : [];
    const codes = einmal(
        befunde.filter((f) => f?.traffic === ampel).map((f) => String(f?.rule ?? f?.type ?? '')),
    );
    // Ohne den Text des Fehlers: er nennt den Titel des Befunds, und der
    // traegt Positionen und Mengen des Projekts. Die Regel (`codes`) genuegt.
    return {
        art: grund === 'rot' ? 'projektcheck_rot' : 'projektcheck_kenntnisnahme',
        codes,
        text: `${codes.length} Befund(e)`,
    };
};

/**
 * Der Text eines SONSTIGEN Fehlers, ohne E-Mail-Adressen — er ist bei diesen
 * Fehlern die einzige Spur (Netz, Datenbank, Programmfehler), seine
 * Fehlertexte nennen aber gelegentlich eine Adresse (Codex-Review, P2).
 */
const ohneAdressen = (s: string): string => s.replace(/[^\s<>()"',;:]+@[^\s<>()"',;:]+/g, '[E-Mail]');

/**
 * Der Text eines sonstigen Fehlers, so neutral, wie er fuer bug_reports sein
 * muss (Opus-Nachpruefung, P2/P3):
 *   - nur die erste Zeile und hoechstens 1000 Zeichen, BEVOR ein Regex
 *     laeuft — der Adress-Regex ist auf langen Zeichenketten ohne Trenner
 *     quadratisch, und er laeuft im catch von issueInvoice vor der Anzeige;
 *   - ab „Aktueller Kunde:" weg: assertCustomerIssuable nennt dort den
 *     Zielkunden eines zusammengefuehrten Kunden, oft eine Privatperson;
 *   - Inhalte in Anfuehrungszeichen maskiert: Ueberbuchung, Storno und
 *     gesenkter Anteil nennen den Titel der LV-Position, und der traegt
 *     gelegentlich Objekt- oder Bauherrnamen;
 *   - ohne E-Mail-Adressen.
 */
const neutralerText = (message: unknown): string => {
    const zeile = String(message ?? '').split(/\r?\n/, 1)[0].slice(0, 1000);
    const ohneKunde = zeile.replace(/\s*Aktueller Kunde:.*$/, '');
    const ohneTitel = ohneKunde.replace(/"[^"]*"/g, '"…"').replace(/„[^“”]*[“”]/g, '„…“');
    return ersteZeile(ohneAdressen(ohneTitel));
};

/**
 * Welche Sperre ein Fehler beim Ausstellen ist — oder null: wenn er sich
 * schon selbst meldet (Tor, Entwurf) oder gar kein Fehler ist.
 */
export const ordneSperreEin = (e: unknown): AusstellSperre | null => {
    try {
        if (!istFehler(e) || meldetSichSelbst(e)) return null;
        if (istValidierung(e)) return validierungsSperre(e.issues as ValidationMessage[]);
        if (istProjektcheck(e)) return projektcheckSperre(e);
        if (istProjektInBearbeitung(e)) {
            // Ohne Text: er nennt den Kollegen, der das Projekt offen hat.
            return { art: 'projekt_in_bearbeitung', codes: [], text: '' };
        }
        // Ein Datenbankfehler traegt seinen Code (42501, PGRST116, …) — er
        // sagt in der Zaehlung mehr als der Text.
        const code = typeof e.code === 'string' || typeof e.code === 'number' ? String(e.code) : '';
        return { art: 'andere', codes: einmal([code]), text: neutralerText(e.message) };
    } catch {
        // Ein Fehler, der beim Lesen selbst wirft (Getter), bleibt ungezaehlt.
        return null;
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Meldung
// ═══════════════════════════════════════════════════════════════════════════

/** Was die Meldung ueber den Versuch weiss — ohne zusaetzliche Abfrage. */
export interface AusstellKontext {
    belegId: string;
    /** Nur, wenn issueInvoice den Beleg schon geladen hatte; sonst holt die Abfrage sie per Join. */
    documentType?: DocumentType;
}

/**
 * Kein `@` und kein Zeilenumbruch in dem, was aus dem Fehler oder vom Aufrufer
 * kommt. `buildFingerprint` (bugReportService) nimmt die erste Stackzeile mit
 * `@` oder `at …` in den Fingerabdruck auf: stuende eine E-Mail-Adresse im
 * Text, landete jede Wiederholung in einer eigenen Zeile, und
 * `occurrence_count` zaehlte nicht mehr hoch.
 */
const saeubere = (s: string): string => s.replace(/@/g, '(at)').replace(/\s*[\r\n]+\s*/g, ' ');

/**
 * Der Titel: Art UND Beleg in der MELDUNG, nicht nur im Stack. `report_bug`
 * fasst nach Fingerabdruck (Meldung + erste Stackzeile mit `@`/`at `)
 * zusammen; der Stack hier hat keine solche Zeile. So ist jede Art je Beleg
 * eine eigene Zeile in `bug_reports`, und ein erneuter Versuch zaehlt sie
 * hoch.
 */
export const ausstellSperrTitel = (art: AusstellSperrArt, belegId: string): string =>
    `[Ausstellen gesperrt] ${art} — Beleg ${saeubere(String(belegId || '?'))}`;

/** Die erste Stackzeile — daran erkennt die Ueberwachung die Familie. */
export const ausstellSperrErsteZeile = (art: AusstellSperrArt): string => `Sperre beim Ausstellen: ${art}`;

export const ausstellSperrMeldung = (sperre: AusstellSperre, kontext: AusstellKontext): TorMeldung => {
    const belegId = saeubere(String(kontext?.belegId || '?'));
    const codes = sperre.codes.map(saeubere);
    const documentType = kontext?.documentType ? saeubere(String(kontext.documentType)) : null;
    return {
        message: ausstellSperrTitel(sperre.art, belegId),
        stack: kappeStack(
            [
                ausstellSperrErsteZeile(sperre.art),
                `Belegart: ${documentType ?? 'unbekannt'}`,
                `Codes: ${codes.length > 0 ? codes.join(', ') : '—'}`,
                `Text: ${kappeText(saeubere(sperre.text)) || '—'}`,
                JSON.stringify({ art: sperre.art, codes, belegId, documentType }),
            ].join('\n'),
        ),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Melden — wirft nie
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Hoechstens so viele Sperrmeldungen je Sitzung. Der Hintergrundkanal hat
 * einen GEMEINSAMEN Deckel (20 je Sitzung, bugReportService); ein Kollege, der
 * zehnmal an derselben Sperre haengt, darf ihn nicht fuer die Integritaets-
 * meldungen des Tors aufbrauchen.
 */
export const MAX_SPERR_MELDUNGEN = 10;

/**
 * Eigenes Kontingent, wie beim Entwurf (entwurfAusJournal): dieselbe Sperre am
 * selben Beleg hoechstens einmal je 5 Minuten. Ein zweiter Klick gleich danach
 * ist kein neuer Befund; nach der Sperrzeit zaehlt er `occurrence_count` hoch.
 */
const kontingent = meldeKontingent(MAX_SPERR_MELDUNGEN, 5 * 60_000);

const zaehler = { gedrosselt: 0 };

/** Was das Kontingent zurueckhielt — ein Modul-Zaehler wie `entwurfZaehler`. */
export const ausstellSperrZaehler = (): { gedrosselt: number } => ({ ...zaehler });

/** Nur fuer Tests: Kontingent und Zaehler zuruecksetzen. */
export const resetAusstellSperren = (): void => {
    kontingent.zuruecksetzen();
    zaehler.gedrosselt = 0;
};

export interface AusstellSperrDeps {
    melde: (m: TorMeldung) => void | Promise<void>;
    jetzt: () => number;
}

export const STANDARD_SPERR_DEPS: AusstellSperrDeps = {
    // Erst beim Aufruf aufgeloest, nicht beim Laden des Moduls.
    melde: (m) => meldeUeberHintergrundkanal(m),
    jetzt: () => Date.now(),
};

/**
 * Meldet einen gescheiterten Ausstellversuch, ohne zu warten
 * (fire-and-forget, wie `meldeOhneWarten` im Entwurf). Baut die Meldung erst
 * hier; auch das darf nicht werfen. Gibt nichts zurueck und wirft nie — der
 * Aufrufer wirft danach seinen Originalfehler weiter.
 */
export const meldeAusstellSperre = (
    fehler: unknown,
    kontext: AusstellKontext,
    deps: AusstellSperrDeps = STANDARD_SPERR_DEPS,
): void => {
    try {
        const sperre = ordneSperreEin(fehler);
        if (!sperre) return;
        const m = ausstellSperrMeldung(sperre, kontext);
        if (!kontingent.darf(m, deps.jetzt())) {
            zaehler.gedrosselt += 1;
            return;
        }
        void sicherMelden(deps, m);
    } catch {
        // Die Meldung ist eine Zugabe: sie darf die Ausstellung nie beruehren.
    }
};
